import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Navbar from "../components/Navbar/Navbar";
import { apiCall } from "../api/apiCall";
import { Category } from "../models/category";
import { User } from "../models/user";
import generateToastContainer from "../utils/ToastContainer";
import "./../home/MemberTable.css";
import TopHeader from "../components/Navbar/TopHeader";
import FeatureGate from "../components/FeatureGate";
import { useTranslation } from "../i18n/UseTranslation";
import { CategoryIcon } from "../utils/CategoryIcons";
import { getAuth, onAuthStateChanged } from "firebase/auth";
interface CompanyGroup {
  companyId: string;
  companyName: string;
  categories: Category[];
}

type CategoriesTranslations = {
  noSearchMatch: string;
  typeName: string;
  categorySingle: string;
  categoryPlural: string;
  page: string;
};

const PAGE_SIZE = 20;

function groupByCompany(
  categories: Category[],
  noCompanyLabel: string,
): CompanyGroup[] {
  const map = new Map<string, CompanyGroup>();

  for (const category of categories) {
    const companyId = category.company?.uuid ?? "__no_company__";

    const companyName = category.company?.name ?? noCompanyLabel;

    if (!map.has(companyId)) {
      map.set(companyId, {
        companyId,
        companyName,
        categories: [],
      });
    }

    map.get(companyId)!.categories.push(category);
  }

  return Array.from(map.values()).sort((a, b) =>
    a.companyName.localeCompare(b.companyName),
  );
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

interface CompanyTableProps {
  group: CompanyGroup;
  searchQuery: string;
  colorIndex: number;
  isAccountant: boolean;
  translations: CategoriesTranslations;
}

const COLOR_CLASSES = [
  "avatar-blue",
  "avatar-teal",
  "avatar-amber",
  "avatar-purple",
  "avatar-coral",
];

function CompanyTable({
  group,
  searchQuery,
  colorIndex,
  isAccountant,
  translations,
}: CompanyTableProps) {
  const { t } = useTranslation();

  const translation = t.categoryTypes;

  const [open, setOpen] = useState(true);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (!query) {
      return group.categories;
    }

    return group.categories.filter((category) => {
      const rawType = category.item_type || "";
      const translatedType =
        translation[rawType.toLowerCase() as keyof typeof translation] ||
        rawType;

      const companyName = category.company?.name || "";

      return (
        translatedType.toLowerCase().includes(query) ||
        companyName.toLowerCase().includes(query)
      );
    });
  }, [group.categories, searchQuery, translation]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const currentPage = Math.min(page, totalPages);

  const slicedCategories = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const avatarColor = COLOR_CLASSES[colorIndex % COLOR_CLASSES.length];

  return (
    <div className="company-block">
      <button
        type="button"
        className="company-header"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
      >
        <div className={`company-avatar ${avatarColor}`}>
          {initials(group.companyName)}
        </div>

        <span className="company-name">{group.companyName}</span>

        <span className={`company-count ${avatarColor}`}>
          {filtered.length}{" "}
          {filtered.length === 1
            ? translations.categorySingle
            : translations.categoryPlural}
          {searchQuery && filtered.length !== group.categories.length
            ? ` / ${group.categories.length}`
            : ""}
        </span>

        <span className={`chevron-icon ${open ? "chevron-open" : ""}`}>
          &#8964;
        </span>
      </button>

      {open && (
        <div className="table-container">
          {filtered.length === 0 ? (
            <p className="empty-state">{translations.noSearchMatch}</p>
          ) : (
            <>
              <div className="table-scroll">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>{translations.typeName}</th>
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {slicedCategories.map((category) => (
                      <tr
                        key={category.uuid}
                        className={isAccountant ? "" : "user-row"}

                        style={{
                          cursor: isAccountant ? "default" : "pointer",
                        }}
                      >
                        <td>
                          <div className="user-cell">
                            <div className={`row-avatar ${avatarColor}`}>
                              <CategoryIcon
                                itemType={category.item_type}
                                size={16}
                              />
                            </div>

                            <span>
                              {category.item_type
                                ? translation[
                                    category.item_type.toLowerCase() as keyof typeof translation
                                  ] || category.item_type
                                : "Autre"}
                            </span>
                          </div>
                        </td>

                        <td className="arrow-cell">
                          {!isAccountant && <>&#13;</>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <span className="pagination-info">
                    {translations.page} {currentPage} / {totalPages}
                  </span>

                  <div className="pagination-controls">
                    <button
                      type="button"
                      className="pg-btn"
                      disabled={currentPage === 1}
                      onClick={() =>
                        setPage((previous) => Math.max(1, previous - 1))
                      }
                    >
                      ‹
                    </button>

                    {Array.from({ length: totalPages }, (_, index) => index + 1)
                      .filter(
                        (pageNumber) =>
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          Math.abs(pageNumber - currentPage) <= 1,
                      )
                      .reduce<(number | "…")[]>(
                        (accumulator, pageNumber, index, array) => {
                          if (
                            index > 0 &&
                            pageNumber - (array[index - 1] as number) > 1
                          ) {
                            accumulator.push("…");
                          }

                          accumulator.push(pageNumber);
                          return accumulator;
                        },
                        [],
                      )
                      .map((pageNumber, index) =>
                        pageNumber === "…" ? (
                          <span key={`dots-${index}`} className="pg-dots">
                            …
                          </span>
                        ) : (
                          <button
                            type="button"
                            key={pageNumber}
                            className={`pg-btn ${
                              pageNumber === currentPage ? "pg-btn--active" : ""
                            }`}
                            onClick={() => setPage(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        ),
                      )}

                    <button
                      type="button"
                      className="pg-btn"
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setPage((previous) =>
                          Math.min(totalPages, previous + 1),
                        )
                      }
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const translations = t.categoriesPage;

  const [searchQuery, setSearchQuery] = useState("");

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const {
    data: currentUser,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
  } = useQuery<User, Error>({
    queryKey: ["me"],
    queryFn: () =>
      apiCall<User>("/users/me", {
        method: "GET",
      }),
    enabled: isAuthReady,
  });

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    error: categoriesError,
  } = useQuery<Category[], Error>({
    queryKey: ["fetchAllCategories"],
    queryFn: () =>
      apiCall<Category[]>("/categories", {
        method: "GET",
      }),
    enabled: isAuthReady,
  });

  const userToGlimpse = currentUser as any;

  const userSubscription =
    userToGlimpse?.Company?.Subscription ||
    userToGlimpse?.company?.Subscription ||
    userToGlimpse?.company?.subscription;

  const currentPlan = !currentUser
    ? "none"
    : userSubscription?.status === "active"
      ? userSubscription.plan_code
      : "free";

  const roleName = currentUser?.role?.name;
  const isAccountant = roleName === "accountant";

  const isLoading = isUserLoading || isCategoriesLoading;

  const isError = isUserError || isCategoriesError;

  const categories = useMemo(() => categoriesData ?? [], [categoriesData]);

  const groups = useMemo(
    () => groupByCompany(categories, translations.noCompany),
    [categories, translations.noCompany],
  );

  const stats = useMemo(
    () => ({
      total: categories.length,
      companies: groups.length,
    }),
    [categories, groups],
  );

  if (isLoading) {
    return (
      <div className="dashboard-wrapper">
        <Navbar />
        <TopHeader />

        <div className="dashboard-content">
          <div className="page-header">
            <h1>{translations.title}</h1>
          </div>

          <FeatureGate
            userPlan={currentPlan}
            requiredPlan="free"
            featureName={translations.featureName}
          >
            <div className="loading-grid">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="skeleton-block" />
              ))}
            </div>
          </FeatureGate>
        </div>
      </div>
    );
  }

  if (isError) {
    const errorMessage =
      userError?.message ||
      categoriesError?.message ||
      translations.unknownError;

    return (
      <div className="dashboard-wrapper">
        <Navbar />
        <TopHeader />

        <div className="dashboard-content">
          <div className="page-header">
            <h1>{translations.title}</h1>
          </div>

          <FeatureGate
            userPlan={currentPlan}
            requiredPlan="free"
            featureName={translations.featureName}
          >
            <div className="error-state">
              <span className="error-icon">&#9888;</span>

              <p>
                {translations.loadingError.replace("{message}", errorMessage)}
              </p>
            </div>
          </FeatureGate>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Navbar />
      <TopHeader />

      <div className="dashboard-content">
        <FeatureGate
          userPlan={currentPlan}
          requiredPlan="free"
          featureName={translations.featureName}
        >
          <div className="page-header" style={{ alignItems: "center" }}>
            <div>
              <h1>{translations.title}</h1>

              <p className="page-subtitle">
                {isAccountant
                  ? translations.accountantSubtitle
                  : translations.managementSubtitle}
              </p>
            </div>

            {!isAccountant && (
              <button
                type="button"
                className="add-policy-submit"
                style={{
                  padding: "10px 20px",
                  height: "auto",
                  borderRadius: "10px",
                  fontSize: "0.95rem",
                }}
                onClick={() => navigate("/add-category")}
              >
                + {translations.addCategory}
              </button>
            )}
          </div>

          <div
            className="stats-row"
            style={{
              gridTemplateColumns: "repeat(2, 1fr)",
            }}
          >
            <div className="stat-card">
              <span className="stat-label">{translations.totalCategories}</span>

              <span className="stat-value">{stats.total}</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">
                {translations.companiesAffected}
              </span>

              <span className="stat-value">{stats.companies}</span>
            </div>
          </div>

          <div className="toolbar">
            <div className="search-wrapper">
              <span className="search-icon">&#128269;</span>

              <input
                type="text"
                className="search-input"
                placeholder={translations.searchPlaceholder}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />

              {searchQuery && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  &#10005;
                </button>
              )}
            </div>
          </div>

          {groups.length === 0 ? (
            <div className="empty-page">
              <p>{translations.noCategories}</p>
            </div>
          ) : (
            groups.map((group, index) => (
              <CompanyTable
                key={group.companyId}
                group={group}
                searchQuery={searchQuery}
                colorIndex={index}
                isAccountant={isAccountant}
                translations={translations}
              />
            ))
          )}
        </FeatureGate>

        {generateToastContainer()}
      </div>
    </div>
  );
}

export default CategoryForm;
